import { Component, inject, Inject, OnInit } from '@angular/core';
import { PaymentPlanService } from '../../../../services/payment-plan/payment-plan.service';
import { take, tap } from 'rxjs/operators';
import { PaymentPlan, PaymentPlanApiService } from '@aiwyn/api';
import { BehaviorSubject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { ClearScheduledPayments } from '../../settings/state';
import { extractFailureContext } from '@aiwyn/shared';
import { PaymentPlanTrackService, PaymentPlanWorkflowEvent } from '@aiwyn/common/payments';

@Component({
  selector: 'app-confirm-delete-payment-plan-modal',
  templateUrl: './confirm-delete-payment-plan-modal.component.html',
  styleUrls: ['./confirm-delete-payment-plan-modal.component.scss'],
  standalone: false,
  providers: [PaymentPlanTrackService],
})
export class ConfirmDeletePaymentPlanModalComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly paymentPlanTrackService = inject(PaymentPlanTrackService);
  requestProcessing$ = new BehaviorSubject<boolean>(false);

  // Set to true once a terminal PAYMENT_PLAN_* event has fired (DEACTIVATE_SUCCEEDED).
  // afterClosed reads this to decide whether to emit PAYMENT_PLAN_CANCELLED.
  private _didEmitTerminalEvent = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmDeletePaymentPlanModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { paymentPlan: PaymentPlan },
    private paymentPlanApiService: PaymentPlanApiService,
    private paymentPlanService: PaymentPlanService,
  ) {}

  ngOnInit(): void {
    this.paymentPlanTrackService.trackWorkflow(
      PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_SUBMITTED,
      { planId: String(this.data.paymentPlan?.id) },
    );

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.paymentPlanTrackService.trackWorkflow(PaymentPlanWorkflowEvent.PAYMENT_PLAN_CANCELLED);
      }
    });
  }

  /**
   * close the modal.
   */
  closeModal(): void {
    this.dialogRef.close();
  }

  /**
   * deactivate the recurring payment then close the modal.
   */
  delete() {
    this.requestProcessing$.next(true);

    const paymentPlan = {
      ...this.data.paymentPlan,
      active: false,
      createdAt: null,
      updatedAt: null,
    };
    const planId = String(this.data.paymentPlan?.id);
    const operationKey = 'PAYMENT_PLAN_DEACTIVATE';
    this.paymentPlanTrackService.startOperation(operationKey);

    this.paymentPlanApiService.deactivatePaymentPlan(paymentPlan).pipe(
      take(1),
      tap({
        next: () => {
          this._didEmitTerminalEvent = true;
          this.paymentPlanTrackService.trackWorkflow(
            PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_SUCCEEDED,
            { planId, operationDurationMs: this.paymentPlanTrackService.endOperation(operationKey) },
          );
          this.paymentPlanService.paymentPlansUpdated$.next();
          this.store.dispatch(new ClearScheduledPayments());
          this.dialogRef.close();
        },
        error: (error: unknown) => {
          this.paymentPlanTrackService.trackWorkflow(
            PaymentPlanWorkflowEvent.PAYMENT_PLAN_DEACTIVATE_FAILED,
            {
              ...extractFailureContext(error),
              planId,
              operationDurationMs: this.paymentPlanTrackService.endOperation(operationKey),
            },
          );
          this.requestProcessing$.next(false);
        },
      }),
    ).subscribe();
  }
}
